import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { endOfDay, startOfDay, subHours, subDays, getUnixTime } from 'date-fns';
import _ from 'lodash';

import './AccountStatement.scss';
import { RootState } from '../../../configs/store';
import WalletSelection from '../WalletSelection/WalletSelection';
import RangeFilterButtons from '../RangeFilterButtons/RangeFilterButtons';
import useIsMobile from 'src/hooks/useIsMobile';
import Details from './components/Details/Details';
import StatementSummary from './components/StatementSummary/StatementSummary';
import SimpleTabs from 'src/components/SimpleTabs/SimpleTabs';

function AccountStatement() {
  const [selectedTab, setSelectedTab] = useState(0);
  const [hours, setHours] = useState(24);
  const [type, setType] = useState({ id: null });
  const [sort, setSort] = useState('desc');
  const [dateRange, setDateRange]= useState([
    {
      startDate: subDays(new Date(), 4),
      endDate: new Date(),
      key: 'selection'
    }
  ]);

  const { customer, historySelectedWallet, localWalletSelected }  = useSelector((state: RootState) => state.auth);
  const { selectedTimezone } = useSelector((state: RootState) => state.odds);
  const { rangeFilterButtons } = useSelector((state: RootState) => state.content);
  const { accountStatement, statistics } = useSelector((state: RootState) => state.account);
  const [activeWallet, setActiveWallet] = useState(null);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const mobile = useIsMobile();

  useEffect(() => {
    if (!historySelectedWallet){
      dispatch.auth.updateHistoryWallet(localWalletSelected);
    }
  }, []);

  useEffect(() => {
    if (!activeWallet && historySelectedWallet){
      setActiveWallet(historySelectedWallet);
    }
  }, [historySelectedWallet]);

  const changeSelectedWallet = useCallback((wallet) => {
    setActiveWallet(wallet);
    dispatch.auth.updateHistoryWallet(wallet);
  }, []);

  useEffect(() => {
    const getStatement = async () => {
      if (hours && hours !== 0 && activeWallet?.typeCode) {
        await dispatch.account.getAccountStatement({
          createdAtFrom: getUnixTime(subHours(startOfDay(new Date()), hours)),
          transactionWallet: activeWallet.typeCode,
          wallet: activeWallet.id,
          ...(type?.id && { transactionType: type.id }),
          order: sort
        });
      }
    };
    customer.id && getStatement();
  }, [hours, activeWallet, type?.id, customer.id, dispatch.account, sort]);

  const handleTabChange = (e, value) => {
    e.preventDefault();
    setSelectedTab(value);
  };

  const changeHours = (e) => {
    e.preventDefault();
    setHours(+e.target.name);
  };

  const onChangeDate = (range) => {
    setDateRange(range);
  };

  const handleApply = () => {
    setHours(0);
    dispatch.account.getAccountStatement({
      createdAtFrom: getUnixTime(startOfDay(dateRange[0]?.startDate)),
      createdAtTo: getUnixTime(endOfDay(dateRange[0]?.endDate)),
      wallet: activeWallet.id,
      ...(type?.id && { transactionType: type.id })
    });
  };

  const clearDateRanges = () => {
    setDateRange([
      {
        startDate: new Date(),
        endDate: new Date(),
        key: 'selection'
      }
    ]);
  };

  const statisticsByWallet = useCallback(() => {
    if (activeWallet){

      const statsIndex = _.findIndex(statistics, { id: activeWallet.id });
      return statistics[statsIndex];
    } else {
      return null;
    }
  }, [activeWallet]);

  const renderTabs = () => {
    return (
      <SimpleTabs
        labels={ [{ label: t('accountHistory.summary') }, { label: t('accountHistory.details') }] }
        onChange={ handleTabChange }
        containerClass="statement"
        defaultTab={ selectedTab }
        variant="fullWidth"
        showIndicator
      />
    );
  };

  const renderWalltetAndFilter = () => {
    return (
      <div className={ mobile ? '' : 'statement__inline' }>
        <WalletSelection
          activeWallet={ activeWallet }
          customer={ customer }
          className="statement"
          changeSelectedWallet={ changeSelectedWallet }
        />
        <RangeFilterButtons
          filterButtons={ rangeFilterButtons }
          onButtonClick={ changeHours }
          dateChanges= { onChangeDate }
          dateRange={ dateRange }
          onRangeApply={ handleApply }
          selectedButton={ hours }
          onRangeClear={ clearDateRanges }
          type={ type }
          setType={ setType }
          hours={ hours }
          sort={ sort }
          setSort={ setSort }
          showRange
        />
      </div>
    );
  };

  const renderStatments = () => {
    return (
      <div className="statement__container">
        {
          (!mobile || selectedTab === 0) && statistics.length > 0 && (
            <div className="statement__summary">
              <StatementSummary statistics={ statisticsByWallet() } divisor={ activeWallet.divisor } />
            </div>
          )
        }
        {
          (!mobile || selectedTab === 1) && (
            <div className="statement__details-container">
              <h2 className="statement-summary__title">{ t('accountHistory.detailsDesc') }</h2>
              <Details accountStatement={ accountStatement } selectedTimezone={ selectedTimezone } />
            </div>
          )
        }
      </div>
    );
  };

  return (
    <div className="statement">
      { mobile && renderTabs() }
      { activeWallet && renderWalltetAndFilter() }
      { activeWallet && renderStatments() }
    </div>
  );
}

export default AccountStatement;
